/**
 * Hook for managing skills + MCP guidelines acceptance in the assistant.
 * Connector picker state is per-conversation now — see `useAgentConnectors`
 * invoked from `ConversationRuntimeInner` in `DatasetAssistantV2`.
 */
import { useCallback } from "react";
import type { AssistantApi } from "~/components/assistant/api/AssistantApi";
import { useMcpGuidelines } from "~/components/assistant/hooks/useMcpGuidelines";
import { useSkillsFeature } from "~/components/assistant/hooks/useSkillsFeature";
import useUser from "~/contexts/user";
import { useConversationSkills } from "~/pages/datasets/components/assistant/hooks/useConversationSkills";
import {
  createUserSkill as postUserSkill,
  deleteUserSkill,
  detachAgentSkill,
  detachDatasetSkill,
  getSkill as fetchSkill,
  invalidateAllSkillLists,
  type PartialSkill,
  type Skill,
  type SkillFields,
  updateSkill as patchSkill,
} from "~/services/skills";

interface UseAssistantV2FeaturesOptions {
  api: AssistantApi;
  contextId: string;
  /**
   * Agent UUID of the current conversation. Drives the AGENT-scope leg of
   * the picker union. Null/undefined disables AGENT fetching.
   */
  agentId?: string | null;
  /**
   * Dataset UUID of the current conversation. Drives the DATASET-scope leg
   * of the picker union. Null/undefined disables DATASET fetching.
   */
  datasetId?: string | null;
  enableSkills?: boolean;
}

export function useAssistantFeatures({
  api,
  agentId,
  datasetId,
  enableSkills = false,
}: UseAssistantV2FeaturesOptions) {
  const skillsFeatureFlag = useSkillsFeature();
  const skillsEnabled = enableSkills && skillsFeatureFlag;

  const {
    hasAccepted: mcpGuidelinesAccepted,
    isAccepting: isAcceptingGuidelines,
    acceptGuidelines,
  } = useMcpGuidelines(api);

  const user = useUser();
  const skillsAgentId = skillsEnabled && agentId ? agentId : null;
  const skillsDatasetId = skillsEnabled && datasetId ? datasetId : null;
  const skillsUserId = skillsEnabled ? (user?.email ?? null) : null;
  const { skills, isLoading: isLoadingSkills } = useConversationSkills(
    skillsAgentId,
    skillsDatasetId,
    skillsUserId
  );

  const createUserSkill = useCallback(async (fields: SkillFields) => {
    await postUserSkill(fields);
    await invalidateAllSkillLists();
  }, []);

  const updateSkill = useCallback(async (skillId: string, fields: Partial<SkillFields>) => {
    await patchSkill(skillId, fields);
    await invalidateAllSkillLists();
  }, []);

  const deleteSkill = useCallback(async (skill: PartialSkill) => {
    switch (skill.scope) {
      case "USER":
        await deleteUserSkill(skill.id);
        break;
      case "AGENT":
        if (!skill.scope_id) {
          throw new Error(`AGENT-scope skill ${skill.id} is missing scope_id`);
        }
        await detachAgentSkill(skill.scope_id, skill.id);
        break;
      case "DATASET":
        if (!skill.scope_id) {
          throw new Error(`DATASET-scope skill ${skill.id} is missing scope_id`);
        }
        await detachDatasetSkill(skill.scope_id, skill.id);
        break;
      default:
        throw new Error(`Cannot delete ${skill.scope}-scope skill from this surface`);
    }
    await invalidateAllSkillLists();
  }, []);

  const getSkill = useCallback(async (skillId: string): Promise<Skill> => fetchSkill(skillId), []);

  return {
    skillsEnabled,
    skills,
    isLoadingSkills,
    createUserSkill,
    updateSkill,
    deleteSkill,
    getSkill,
    mcpGuidelinesAccepted,
    isAcceptingGuidelines,
    acceptGuidelines,
  };
}
